import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserRole, getUserContext } from '../../auth';
import HeaderUI from '../components/HeaderUI';
import CrearClienteForm from '../../Admin/components/CrearClienteForm';
import CrearLeadVendedorDialog from '../../Vendedor/components/CrearLeadVendedorDialog';
import ClienteDialog from '../../Vendedor/components/ClienteDialog';
import { apiFetch } from '../../api';
import DuplicateResolveDialog from '../../Admin/components/DuplicateResolveDialog';
import socket from '../../socket';

export default function Header({ 
    onLogout, 
    onSwitchProfile,
    moduleName = "VENTAS",
    disableNotifications = false,
    onBackToMenu = null,
    hideUserInfoInMenu = false
}) {
    const navigate = useNavigate();
    const userCtx = getUserContext();
    const rol = getUserRole();

    // 1. Nombre para el Texto (Empresa)
    const empresaNombre = userCtx?.nombre || 'Empresa';

    // 2. Nombre para el Avatar (Perfil Vendedor/Admin)
    const profileName = userCtx?.usuario || 'Usuario';
    // IDs seguros para consultas
    const currentUserId = userCtx?.id; // ID del perfil (vendedor o admin)
    const currentVendedorId = rol === "Vendedor"
        ? userCtx?.vendedor_id ?? userCtx?.id_vendedor ?? userCtx?.id ?? null
        : null;

    // --- ESTADOS DE NOTIFICACIONES ---
    const [notifAnchorEl, setNotifAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const notifOpen = Boolean(notifAnchorEl);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    // state for Crear Cliente modal (so header can open the same panel used in Admin)
    const initial = {
        nombre: '',
        telefono: '',
        email: '',
        consulta: '',
        consultas: [],
        obtenido_de: '',
        observacion: ''
    };
    const [crearOpen, setCrearOpen] = useState(false);
    const [form, setForm] = useState(initial);
    const [loadingCrear, setLoadingCrear] = useState(false);
    const [errorCrear, setErrorCrear] = useState(null);
    const [duplicateOpenCrear, setDuplicateOpenCrear] = useState(false);
    const [duplicatePayloadCrear, setDuplicatePayloadCrear] = useState(null);
    const [notificationClientOpen, setNotificationClientOpen] = useState(false);
    const [notificationClientData, setNotificationClientData] = useState(null);
    const [notificationClientLoading, setNotificationClientLoading] = useState(false);
    const [notificationClientSaving, setNotificationClientSaving] = useState(false);

    // --- CARGA DE NOTIFICACIONES ---
    const fetchNotifications = useCallback(async () => {
        if (!currentUserId || disableNotifications) return;
        try {
            // 1. Obtener lista
            const resList = await apiFetch(`/api/notifications/`, { credentials: 'include' });
            if (resList.ok) {
                const data = await resList.json();
                setNotifications(Array.isArray(data) ? data : []);
            }

            // 2. Obtener conteo no leídas
            const resCount = await apiFetch(`/api/notifications/count`, { credentials: 'include' });
            if (resCount.ok) {
                const dataCount = await resCount.json();
                setUnreadCount(dataCount.count || 0);
            }
        } catch (e) {
            console.error("Error fetching notifications:", e);
        }
    }, [currentUserId, disableNotifications]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // --- HANDLERS NOTIFICACIONES ---
    const handleNotifClick = (event) => {
        setNotifAnchorEl(event.currentTarget);
        // Al abrir, refrescamos por si acaso
        fetchNotifications();
    };

    const handleNotifClose = () => {
        setNotifAnchorEl(null);
    };

    const handleNotificationItemClick = () => {};

    const handleMarkNotificationRead = async (notif) => {
        if (!notif.leido) {
            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, leido: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
            try {
                await apiFetch(`/api/notifications/${notif.id}/read`, {
                    method: 'PATCH',
                    credentials: 'include'
                });
            } catch (e) {
                console.error("Error marcando leído:", e);
            }
        }
    };

    const handleViewNotificationClient = async (notif) => {
        const clienteId = notif?.cliente_id;
        if (!clienteId) {
            alert("No se pudo abrir el cliente: la notificación no tiene cliente asociado.");
            return;
        }
        setNotifAnchorEl(null);
        setNotificationClientOpen(true);
        setNotificationClientData(null);
        setNotificationClientLoading(true);
        try {
            const res = await apiFetch(`/api/clientes/${clienteId}`, { credentials: 'include' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setNotificationClientData(data);
        } catch (e) {
            console.error("Error abriendo cliente de notificación:", e);
            setNotificationClientOpen(false);
            alert("No se pudo abrir el cliente de la notificación.");
        } finally {
            setNotificationClientLoading(false);
        }
    };

    const handleSaveNotificationClient = async (cliente) => {
        if (!cliente?.id) return null;
        setNotificationClientSaving(true);
        try {
            const payload = {
                nombre: cliente.nombre,
                telefono: cliente.telefono || null,
                email: cliente.email || null,
                obtenido_de: cliente.obtenido_de || null,
                observacion: cliente.observacion || null,
            };
            const res = await apiFetch(`/api/clientes/${cliente.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            const updated = await res.json();
            setNotificationClientData(updated);
            return updated;
        } finally {
            setNotificationClientSaving(false);
        }
    };

    const handleResolveNotificationAction = async (notif) => {

        if (notif.tipo === 'FUTURO_PROGRAMADO' && rol === 'Admin' && notif.vendedor_activo === false) {
            try {
                window.dispatchEvent(new CustomEvent('open_reassign_for_ven_clien', {
                    detail: {
                        ven_clien_id: notif.ven_clien_id,
                        cliente_id: notif.cliente_id,
                        vendedor_id: notif.vendedor_id,
                        notif_id: notif.id,
                        shouldReopen: true,
                        vendedor_nombre: notif.vendedor_nombre ?? notif.nombre_vendedor ?? notif.vendedor?.nombre,
                    }
                }));
            } catch (e) {
                console.error('Error dispatching open_reassign event:', e);
            }
            setNotifAnchorEl(null);
            return;
        }

        try {
            const resolveRes = await apiFetch(`/api/notifications/${notif.id}/resolve`, {
                method: 'PATCH',
                credentials: 'include'
            });
            if (!resolveRes.ok) {
                const err = await resolveRes.json().catch(() => ({}));
                throw new Error(err.detail || "Error al reactivar cliente");
            }

            //Actualizar UI Local (quitar de la lista inmediatamente)
            setNotifications(prev => prev.filter(n => n.id !== notif.id));
            if (!notif.leido) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

        } catch (e) {
            console.error("Error resolviendo notificación:", e);
            alert("No se pudo procesar la solicitud: " + e.message);
        }
    };

    useEffect(() => {
        if (disableNotifications) return;
        const handleNewNotification = (data) => {
            if (data.fecha_programada) {
                const fechaProg = new Date(data.fecha_programada);
                const ahora = new Date();
                if (fechaProg > new Date(ahora.getTime() + 60000)) {
                    return;
                }
            }

            // Si Admin recibe FUTURO_PROGRAMADO y el vendedor asociado está inactivo,
            // abrimos inmediatamente el modal de reasignación de forma obligatoria.
            if (rol === 'Admin' && data.tipo === 'FUTURO_PROGRAMADO' && data.vendedor_activo === false) {
                try {
                    window.dispatchEvent(new CustomEvent('open_reassign_for_ven_clien', {
                        detail: {
                            ven_clien_id: data.ven_clien_id,
                            cliente_id: data.cliente_id,
                            vendedor_id: data.vendedor_id,
                            notif_id: data.id,
                            shouldReopen: true,
                            vendedor_nombre: data.vendedor_nombre ?? data.nombre_vendedor ?? data.vendedor?.nombre,
                        }
                    }));
                } catch (e) {
                    console.error('Error dispatching open_reassign event:', e);
                }
            }
            // Evitar duplicados por seguridad
            setNotifications(prev => {
                if (prev.some(n => n.id === data.id)) return prev;
                return [data, ...prev];
            });
            setUnreadCount(prev => prev + 1);
        };

        const handleResolveNotification = (data) => {
            setNotifications(prev => prev.filter(n => n.id !== data.id));
            fetchNotifications();
        };

        socket.on("new_notification", handleNewNotification);
        socket.on("resolve_notification", handleResolveNotification);

        return () => {
            socket.off("new_notification", handleNewNotification);
            socket.off("resolve_notification", handleResolveNotification);
        };
    }, [rol, fetchNotifications, disableNotifications]);

    const handleDeleteNotificationAction = async (notif) => {
        if (!window.confirm("¿Eliminar esta notificación?")) return;

        try {
            // 1. Llamada a la API para borrar
            const res = await apiFetch(`/api/notifications/${notif.id}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error("No se pudo eliminar la notificación");
            }

            // 2. Actualizar UI Local (quitar de la lista)
            setNotifications(prev => prev.filter(n => n.id !== notif.id));
            
            // Si no estaba leída, bajamos el contador
            if (!notif.leido) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

        } catch (e) {
            console.error("Error eliminando notificación:", e);
            alert("Error: " + e.message);
        }
    };

    const handleOpenCrear = () => setCrearOpen(true);
    const handleCloseCrear = () => { setCrearOpen(false); setErrorCrear(null); setForm(initial); };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'telefono') {
            const digits = (value || '').replace(/\D/g, '').slice(0, 10);
            setForm(prev => ({ ...prev, telefono: digits }));
            return;
        }
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        setErrorCrear(null);
        setLoadingCrear(true);

        const payload = {
            ...form,
            email: form.email.trim() === '' ? null : form.email?.trim(),
            obtenido_de: form.obtenido_de.trim() === '' ? null : form.obtenido_de?.trim(),
            observacion: form.observacion?.trim() ? form.observacion.trim() : null,
            consulta: form.consultas?.[0]?.vehiculo_consultado || null,
        };
        const vendedorId = rol === "Vendedor"
            ? userCtx?.vendedor_id ?? userCtx?.id_vendedor ?? userCtx?.id ?? null
            : null;
        if (vendedorId) payload.vendedor_id = Number(vendedorId);

        try {
            const res = await apiFetch(`/api/clientes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });
            if (!res.ok) {
                if (res.status === 409) {
                    let json = null;
                    try { json = await res.json(); } catch (e) { json = null; }
                    setDuplicatePayloadCrear(json);
                    setDuplicateOpenCrear(true);
                    setLoadingCrear(false);
                    return;
                }
                const txt = await res.text();
                throw new Error(txt || 'Error creando cliente');
            }

            try {
                const createdClient = await res.json();
                if (createdClient?.id && vendedorId) {
                    window.dispatchEvent(new CustomEvent('created_cliente_assigned', {
                        detail: { clientId: Number(createdClient.id), vendedorId: Number(vendedorId) }
                    }));
                }
            } catch (err) {
                // response not JSON; no local event needed
            }

            setForm(initial);
            setCrearOpen(false);
        } catch (err) {
            setErrorCrear(err.message || 'Error creando cliente');
        } finally {
            setLoadingCrear(false);
        }
    };

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleLogoutAction = () => {
        handleMenuClose();
        onLogout();
    };

    const handleSwitchAction = () => {
        handleMenuClose();
        if (onSwitchProfile) onSwitchProfile();
    };

    const handleLogoClick = () => {
        navigate('/inicio');
    };

    return (
        <>
            <HeaderUI
                empresaNombre={empresaNombre}
                profileName={profileName}
                rol={rol}

                // Props Menu Usuario
                anchorEl={anchorEl}
                open={open}
                onMenuClick={handleMenuClick}
                onMenuClose={handleMenuClose}
                onLogout={handleLogoutAction}
                onSwitchProfile={handleSwitchAction}
                onCrearClienteClick={handleOpenCrear}

                // Props Notificaciones
                notifAnchorEl={notifAnchorEl}
                notifOpen={notifOpen}
                onNotifClick={handleNotifClick}
                onNotifClose={handleNotifClose}
                notifications={notifications}
                unreadCount={unreadCount}
                onNotificationItemClick={handleNotificationItemClick}
                onResolveNotification={handleResolveNotificationAction}
                onMarkNotificationRead={handleMarkNotificationRead}
                onViewNotificationClient={handleViewNotificationClient}
                onDeleteNotification={handleDeleteNotificationAction}

                moduleTitle={moduleName}
                showNotifications={!disableNotifications}
                onBackToMenu={onBackToMenu}
                onLogoClick={handleLogoClick}
                hideUserInfoInMenu={hideUserInfoInMenu}
            />

            {rol === "Vendedor" ? (
                <CrearLeadVendedorDialog
                    open={crearOpen}
                    onClose={handleCloseCrear}
                    currentVendedorId={currentVendedorId}
                    onCreated={({ cliente }) => {
                        if (cliente?.id && currentVendedorId) {
                            window.dispatchEvent(new CustomEvent('created_cliente_assigned', {
                                detail: { clientId: Number(cliente.id), vendedorId: Number(currentVendedorId) }
                            }));
                        }
                    }}
                />
            ) : (
                <CrearClienteForm
                    open={crearOpen}
                    onClose={handleCloseCrear}
                    form={form}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    error={errorCrear}
                    loading={loadingCrear}
                />
            )}
            <DuplicateResolveDialog
                open={duplicateOpenCrear}
                onClose={() => setDuplicateOpenCrear(false)}
                existing={duplicatePayloadCrear?.existing}
                incoming={duplicatePayloadCrear?.incoming}
                match_source={duplicatePayloadCrear?.match_source}
                onResolved={({ kept, created }) => {
                    setDuplicateOpenCrear(false);
                    setForm(initial);
                    setCrearOpen(false);
                }}
            />
            <ClienteDialog
                open={notificationClientOpen}
                onClose={() => setNotificationClientOpen(false)}
                cliente={notificationClientData}
                onChange={setNotificationClientData}
                onSave={handleSaveNotificationClient}
                onOpenSeguimiento={() => {}}
                saving={notificationClientSaving}
                loading={notificationClientLoading}
            />
        </>
    );
}
